<template>
  <div class="lg-gauge" :class="`v-${variant}`">
    <!-- Radial donut -->
    <svg
      v-if="variant === 'radial'"
      class="lg-gauge-radial"
      :width="size"
      :height="size"
      :viewBox="`0 0 ${size} ${size}`"
      role="img"
      :aria-label="ariaLabel"
    >
      <circle class="lg-gauge-track" :cx="c" :cy="c" :r="r" fill="none" :stroke-width="stroke" />
      <circle
        class="lg-gauge-fill"
        :cx="c"
        :cy="c"
        :r="r"
        fill="none"
        :stroke="fillColor"
        :stroke-width="stroke"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashoffset"
        stroke-linecap="round"
        :transform="`rotate(-90 ${c} ${c})`"
      />
      <text class="lg-gauge-text" :x="c" :y="c" text-anchor="middle" dominant-baseline="central">
        {{ displayValue }}
      </text>
    </svg>

    <!-- Horizontal bar -->
    <div v-else class="lg-gauge-bar-wrap">
      <div class="lg-gauge-bar-top">
        <span v-if="label" class="lg-gauge-bar-label">{{ label }}</span>
        <span class="lg-gauge-bar-value">{{ displayValue }}</span>
      </div>
      <div class="lg-gauge-bar-track" role="img" :aria-label="ariaLabel">
        <div class="lg-gauge-bar-fill" :style="{ width: `${pct * 100}%`, background: fillColor }" />
      </div>
    </div>

    <div v-if="label && variant === 'radial'" class="lg-gauge-label">{{ label }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatMetric, type MetricUnit } from '../lib/format-metric.js';

const props = withDefaults(
  defineProps<{
    value: number;
    min?: number;
    max?: number;
    label?: string;
    unit?: MetricUnit;
    variant?: 'radial' | 'bar';
    /** Diameter (radial) in px. */
    size?: number;
    /** Ring thickness (radial) in px. */
    stroke?: number;
    /** Color thresholds on the raw value: >= danger → danger, >= warn → warn. */
    thresholds?: { warn?: number; danger?: number };
  }>(),
  { min: 0, max: 100, unit: 'percent', variant: 'radial', size: 96, stroke: 10 },
);

const pct = computed(() => {
  const span = props.max - props.min;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (props.value - props.min) / span));
});

const displayValue = computed(() => formatMetric(props.value, props.unit));

const ariaLabel = computed(() => `${props.label ? `${props.label}: ` : ''}${displayValue.value}`);

const fillColor = computed(() => {
  const t = props.thresholds;
  if (t?.danger != null && props.value >= t.danger) return 'var(--lg-danger)';
  if (t?.warn != null && props.value >= t.warn) return 'var(--lg-warn)';
  return 'var(--lg-accent)';
});

// Radial geometry
const c = computed(() => props.size / 2);
const r = computed(() => props.size / 2 - props.stroke / 2);
const circumference = computed(() => 2 * Math.PI * r.value);
const dashoffset = computed(() => circumference.value * (1 - pct.value));
</script>

<style scoped>
.lg-gauge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--lg-space-1);
  min-width: 0;
}
.lg-gauge.v-bar {
  align-items: stretch;
  width: 100%;
}

.lg-gauge-track {
  stroke: var(--lg-border);
}
.lg-gauge-fill {
  transition: stroke-dashoffset var(--lg-dur-base) var(--lg-ease);
}
.lg-gauge-text {
  fill: var(--lg-text);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-md);
  font-weight: 600;
}
.lg-gauge-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lg-gauge-bar-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
  width: 100%;
}
.lg-gauge-bar-top {
  display: flex;
  justify-content: space-between;
  gap: var(--lg-space-2);
  font-size: var(--lg-fs-sm);
}
.lg-gauge-bar-label {
  color: var(--lg-text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lg-gauge-bar-value {
  color: var(--lg-text);
  font-family: var(--lg-font-mono);
  flex-shrink: 0;
}
.lg-gauge-bar-track {
  height: 8px;
  border-radius: var(--lg-radius-pill);
  background: var(--lg-bg-surface);
  overflow: hidden;
}
.lg-gauge-bar-fill {
  height: 100%;
  border-radius: var(--lg-radius-pill);
  transition: width var(--lg-dur-base) var(--lg-ease);
}
</style>
